import React, { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate, Outlet, useLocation, Link } from "react-router-dom";
import {
  Button,
  Iconography,
  IconographySizes,
  Loader,
  LoaderSizes,
} from "@hid-galaxy-ui/galaxy-react";
import moment from "moment";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import { getProjectDetailsSagaAction } from "../../../../../redux/saga/projects/saga-actions";
import { projectsSliceAction } from "../../../../../redux/slices/projects";
import {
  getUserListHIDSagaAction,
  getUserListSagaAction,
} from "../../../../../redux/saga/user/saga-actions";
import { useTimelineStepPermissions } from "../../../../../hooks/useTimelineStepPermissions";
import QuickNavigation from "../../../../../common/components/QuickNavigation";
import ProjectDetailsBanner from "./components/ProjectDetailsBanner";
import ProjectDetailsOverviewTab from "./components/ProjectDetailsOverviewTab";
import ProjectDetailsIntegrationTab from "./components/ProjectDetailsIntegrationTab";
import { countPendingIntegrationTasks } from "./utils/buildIntegrationMilestones";
import "./ProjectDetails.scss";

type ProjectDetailsSubTab = "overview" | "performance" | "integration";

const getTechnologyStorageKey = (
  projectName?: string,
  partnerId?: string | number | null
) => {
  if (!projectName) return null;
  const decodedProjectName = decodeURIComponent(projectName);
  // Prefer partner-scoped key; always allow project-only fallback key.
  if (partnerId != null && String(partnerId).trim() !== "") {
    return `project-active-technology:${partnerId}:${decodedProjectName}`;
  }
  return `project-active-technology:${decodedProjectName}`;
};

const readStoredTechnology = (
  projectName?: string,
  partnerId?: string | number | null
) => {
  try {
    const scopedKey = getTechnologyStorageKey(projectName, partnerId);
    if (scopedKey) {
      const scopedValue = sessionStorage.getItem(scopedKey);
      if (scopedValue) return scopedValue;
    }

    // Fallback for older / partner-less writes
    const fallbackKey = getTechnologyStorageKey(projectName, null);
    if (fallbackKey && fallbackKey !== scopedKey) {
      return sessionStorage.getItem(fallbackKey) || "";
    }
  } catch {
    return "";
  }
  return "";
};

const writeStoredTechnology = (
  technology: string,
  projectName?: string,
  partnerId?: string | number | null
) => {
  if (!technology || !projectName) return;
  try {
    const scopedKey = getTechnologyStorageKey(projectName, partnerId);
    if (scopedKey) {
      sessionStorage.setItem(scopedKey, technology);
    }
    // Also write project-only key so restore works even if partnerId is missing on load
    const fallbackKey = getTechnologyStorageKey(projectName, null);
    if (fallbackKey && fallbackKey !== scopedKey) {
      sessionStorage.setItem(fallbackKey, technology);
    }
  } catch {
    // Ignore storage failures (private mode / quota).
  }
};

const formatLastUpdated = (updatedAt?: string | null): string => {
  const format = "MMM DD YYYY - hh:mm A";
  return updatedAt ? moment(updatedAt).format(format) : moment().format(format);
};

const ProjectDetails: React.FC = () => {
  const { projectName, partnerID } = useParams<{
    projectName: string;
    partnerID: string;
  }>();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { projectDetails, status } = useAppSelector((state) => state.projects);
  const { partnerDetails } = useAppSelector((state) => state.partner_details);
  const { userListHID, fetchedUserListPartnerID } = useAppSelector(
    (state) => state.user
  );

  const resolvedPartnerId = partnerID || partnerDetails?.ID || null;
  const decodedProjectName = projectName
    ? decodeURIComponent(projectName)
    : "";

  const [activeTechnology, setActiveTechnology] = useState<string>(() =>
    readStoredTechnology(projectName, resolvedPartnerId)
  );
  const [activeSubTab, setActiveSubTab] =
    useState<ProjectDetailsSubTab>("overview");
  const [selectedIntegrationId, setSelectedIntegrationId] = useState<number | null>(
    null
  );

  const isValidationRoute = location.pathname.includes("/validation/");
  const { canAccessTimeline } = useTimelineStepPermissions();

  const hasStaleProjectDetails =
    Boolean(projectDetails) &&
    projectDetails!.project_name !== decodedProjectName;

  const isLoadingProject =
    status.projectDetailsLoading || hasStaleProjectDetails;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [location.pathname]);

  useEffect(() => {
    setActiveTechnology(readStoredTechnology(projectName, resolvedPartnerId));
    setActiveSubTab("overview");
    setSelectedIntegrationId(null);
  }, [projectName, resolvedPartnerId]);

  useEffect(() => {
    if (!projectName || resolvedPartnerId == null) return;

    // Drop previous project so the UI does not keep showing stale details
    // while the next project request is in flight.
    dispatch(projectsSliceAction.setProjectDetailsLoading(true));
    dispatch(projectsSliceAction.setProjectDetails(null));
    dispatch(projectsSliceAction.setIntegrationSummaries({}));
    dispatch(projectsSliceAction.setIntegrationDetail(null));

    dispatch(
      getProjectDetailsSagaAction(
        decodeURIComponent(projectName),
        Number(resolvedPartnerId)
      )
    );
  }, [projectName, resolvedPartnerId, dispatch]);

  useEffect(() => {
    if (projectName && resolvedPartnerId != null && isValidationRoute) {
      dispatch(
        getProjectDetailsSagaAction(
          decodeURIComponent(projectName),
          Number(resolvedPartnerId)
        )
      );
    }
  }, [projectName, resolvedPartnerId, isValidationRoute, dispatch]);

  useEffect(() => {
    if (userListHID.length === 0) dispatch(getUserListHIDSagaAction());
  }, [dispatch, userListHID.length]);

  useEffect(() => {
    if (partnerID) {
      const pid = Number(partnerID);
      if (!Number.isNaN(pid) && fetchedUserListPartnerID !== pid) {
        dispatch(getUserListSagaAction(pid));
      }
    }
  }, [partnerID, fetchedUserListPartnerID, dispatch]);

  useEffect(() => {
    if (!projectDetails?.technologies.length) return;

    const technologyFromState = location.state?.selectedTechnology as
      | string
      | undefined;
    const technologyFromStorage = readStoredTechnology(
      projectName,
      resolvedPartnerId
    );

    const isValidTechnology = (value?: string | null) =>
      Boolean(
        value &&
          projectDetails.technologies.some((tech) => tech.technology === value)
      );

    setActiveTechnology((currentTab) => {
      // Keep the user's current selection if still valid
      if (isValidTechnology(currentTab)) {
        return currentTab!;
      }
      // sessionStorage survives refresh
      if (isValidTechnology(technologyFromStorage)) {
        return technologyFromStorage;
      }
      // One-time navigation from projects list
      if (isValidTechnology(technologyFromState)) {
        writeStoredTechnology(
          technologyFromState!,
          projectName,
          resolvedPartnerId
        );
        return technologyFromState!;
      }
      return projectDetails.technologies[0].technology;
    });
  }, [
    projectDetails,
    projectName,
    resolvedPartnerId,
    location.state?.selectedTechnology,
  ]);

  // Clear one-time navigation state after it has been applied, so it
  // cannot override a later tab choice when projectDetails refetches.
  useEffect(() => {
    if (!location.state?.selectedTechnology) return;
    navigate(
      {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
      },
      { replace: true, state: null }
    );
  }, [
    location.state?.selectedTechnology,
    location.pathname,
    location.search,
    location.hash,
    navigate,
  ]);

  const handleTechnologyChange = (technology: string) => {
    setActiveTechnology(technology);
    writeStoredTechnology(technology, projectName, resolvedPartnerId);
  };

  const selectedTechnology = useMemo(() => {
    return (
      projectDetails?.technologies.find(
        (tech) => tech.technology === activeTechnology
      ) || projectDetails?.technologies[0]
    );
  }, [projectDetails, activeTechnology]);

  useEffect(() => {
    const integrations = selectedTechnology?.integrations ?? [];
    if (integrations.length === 0) {
      setSelectedIntegrationId(null);
      return;
    }

    setSelectedIntegrationId((currentId) => {
      if (
        currentId &&
        integrations.some((integration) => integration.integration_id === currentId)
      ) {
        return currentId;
      }
      return integrations[0].integration_id;
    });
  }, [selectedTechnology]);

  const selectedIntegration = useMemo(() => {
    return (
      selectedTechnology?.integrations.find(
        (integration) => integration.integration_id === selectedIntegrationId
      ) || selectedTechnology?.integrations[0]
    );
  }, [selectedTechnology, selectedIntegrationId]);

  const lastUpdated = useMemo(
    () => formatLastUpdated(selectedIntegration?.updated_at),
    [selectedIntegration]
  );

  const pendingTaskCount = useMemo(
    () => countPendingIntegrationTasks(selectedIntegration),
    [selectedIntegration]
  );

  const integrationOptions = selectedTechnology?.integrations ?? [];

  const handleBack = () => {
    if (partnerDetails) {
      navigate(`/partner/${partnerDetails.ID}/dashboard/projects`);
    } else {
      navigate(-1);
    }
  };

  if (isValidationRoute && isLoadingProject) {
    return (
      <div className="project-details-page-container">
        <div className="project-details-wrapper project-details-wrapper--centered">
          <Loader variant={LoaderSizes.large} />
        </div>
      </div>
    );
  }

  if (
    !isLoadingProject &&
    (!projectDetails || projectDetails.technologies.length === 0)
  ) {
    return (
      <div className="project-details-page-container">
        <div className="project-details-wrapper">
          <button type="button" className="project-details-breadcrumb" onClick={handleBack}>
            Projects
          </button>
          <div className="project-details-card project-details-card--empty">
            <h1>Project Not Found</h1>
            <p>The project you're looking for could not be found.</p>
          </div>
        </div>
      </div>
    );
  }

  if (isValidationRoute) {
    return <Outlet />;
  }

  if (!canAccessTimeline) {
    return (
      <div className="project-details-page-container">
        <div className="project-details-wrapper project-details-wrapper--centered">
          <div className="project-details-card project-details-card--empty">
            <h1>Permission Denied</h1>
            <p>You do not have permission to view this project overview.</p>
            <Button
              variant="primary"
              label="Back to Projects"
              onClick={handleBack}
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-details-page-container">
      {isLoadingProject || !selectedTechnology ? (
        <div className="project-details-wrapper project-details-wrapper--centered">
          <Loader variant={LoaderSizes.large} />
        </div>
      ) : (
        <div className="project-details-wrapper">
          <nav className="project-details-breadcrumb" aria-label="Breadcrumb">
            <Link
              to={`/partner/${partnerDetails?.ID}/dashboard/projects`}
              className="project-details-breadcrumb__link"
            >
              Projects
            </Link>
            <span className="project-details-breadcrumb__separator" aria-hidden="true">
              <Iconography icon="angleRight" size={IconographySizes.Small} />
            </span>
            <span className="project-details-breadcrumb__current">
              Solution Overview - {projectDetails.project_name}
            </span>
          </nav>

          <ProjectDetailsBanner
            projectName={projectDetails.project_name}
            lastUpdated={lastUpdated}
            technologies={projectDetails.technologies}
            activeTechnology={activeTechnology}
            onTechnologyChange={handleTechnologyChange}
          />

          <div className="project-details-panel">
            <div className="project-details-subtabs">
              <div className="project-details-subtabs__list">
                {(
                  [
                    { id: "overview", label: "Overview" },
                    { id: "performance", label: "Performance Trend" },
                    { id: "integration", label: "Integration Details" },
                  ] as const
                ).map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`project-details-subtabs__tab${
                      activeSubTab === tab.id
                        ? " project-details-subtabs__tab--active"
                        : ""
                    }`}
                    onClick={() => setActiveSubTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>

              <div className="project-details-subtabs__actions">
                <div className="project-details-subtabs__select-wrap">
                  <select
                    className="project-details-subtabs__select"
                    value={selectedIntegrationId ?? ""}
                    disabled={integrationOptions.length === 0}
                    onChange={(event) =>
                      setSelectedIntegrationId(Number(event.target.value))
                    }
                    aria-label="Select integration type"
                  >
                    <option value="" disabled>
                      Select integration type
                    </option>
                    {integrationOptions.map((integration) => (
                      <option
                        key={integration.integration_id}
                        value={integration.integration_id}
                      >
                        {integration.integration}
                      </option>
                    ))}
                  </select>
                  <Iconography
                    icon="chevronDown"
                    size={IconographySizes.Small}
                    className="project-details-subtabs__select-icon"
                  />
                </div>

                {integrationOptions.length > 0 ? (
                  <div className="project-details-subtabs__pending-badge">
                    <span
                      className="project-details-subtabs__pending-dot"
                      aria-hidden="true"
                    />
                    <span>
                      {pendingTaskCount} Pending task
                      {pendingTaskCount === 1 ? "" : "s"}
                    </span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="project-details-panel__content">
              {activeSubTab === "overview" && selectedIntegration ? (
                <ProjectDetailsOverviewTab
                  selectedTechnology={selectedTechnology}
                  selectedIntegration={selectedIntegration}
                />
              ) : null}

              {activeSubTab === "performance" ? (
                <div className="project-details-card project-details-card--empty">
                  <h3 className="project-details-card__title">Performance Trend</h3>
                  <p className="project-details-card__subtitle">
                    Performance metrics for this project will appear here.
                  </p>
                </div>
              ) : null}

              {activeSubTab === "integration" && selectedIntegrationId ? (
                <ProjectDetailsIntegrationTab
                  selectedTechnology={selectedTechnology}
                  integrations={selectedTechnology.integrations}
                  selectedIntegrationId={selectedIntegrationId}
                />
              ) : null}

              {activeSubTab !== "performance" &&
              (!selectedTechnology.integrations.length || !selectedIntegration) ? (
                <div className="project-details-card project-details-card--empty">
                  <p>No integrations available for this technology</p>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
      <QuickNavigation variant="flyout" />
    </div>
  );
};

export default ProjectDetails;
